// src/components/operationCenter/widgets/thesis/ThesisConditionList.tsx
// Compact list of thesis conditions with met/unmet status

import React from "react";
import { CheckCircle2, XCircle, Bot, Calculator, ChevronRight } from "lucide-react";
import type { ThesisCondition } from "@/types/thesis";

interface ThesisConditionListProps {
  conditions: ThesisCondition[];
  maxVisible?: number;
  onConditionClick?: (conditionId: string) => void;
  compact?: boolean;
}

function formatConditionLabel(condition: ThesisCondition): string {
  if (condition.label) {
    return condition.label;
  }

  if (condition.type === "prompt" && condition.prompt) {
    // Truncate long prompts
    return condition.prompt.length > 50
      ? condition.prompt.substring(0, 47) + "..."
      : condition.prompt;
  }

  if (condition.type === "deterministic") {
    const metric = condition.metricKey ?? "Metric";
    const operator = condition.operator ?? ">";
    const threshold = condition.threshold ?? 0;
    const entity = condition.entityId ? ` (${condition.entityId})` : "";
    return `${metric}${entity} ${operator} ${threshold}`;
  }

  return "Condition";
}

function getOperatorSymbol(operator: string): string {
  switch (operator) {
    case ">=":
      return "≥";
    case "<=":
      return "≤";
    case "==":
      return "=";
    case "!=":
      return "≠";
    case "crosses_above":
      return "↗";
    case "crosses_below":
      return "↘";
    default:
      return operator;
  }
}

interface ConditionItemProps {
  condition: ThesisCondition;
  onClick: (() => void) | undefined;
  compact: boolean;
}

const ConditionItem: React.FC<ConditionItemProps> = ({
  condition,
  onClick,
  compact,
}) => {
  const Icon = condition.type === "prompt" ? Bot : Calculator;
  const StatusIcon = condition.isMet ? CheckCircle2 : XCircle;
  const statusColor = condition.isMet ? "text-emerald-400" : "text-red-400";

  if (compact) {
    return (
      <button
        onClick={onClick}
        className="flex items-center gap-1.5 w-full py-1 px-1.5 rounded hover:bg-slate-700/30 transition-colors group"
      >
        <StatusIcon className={`w-3 h-3 flex-shrink-0 ${statusColor}`} />
        <span className="text-[10px] text-slate-300 truncate flex-1 text-left">
          {formatConditionLabel(condition)}
        </span>
        {onClick && (
          <ChevronRight className="w-3 h-3 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
        )}
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className="flex items-start gap-2 w-full py-1.5 px-2 rounded hover:bg-slate-700/30 transition-colors group"
    >
      <StatusIcon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${statusColor}`} />
      <div className="flex-1 min-w-0 text-left">
        <div className="flex items-center gap-1">
          <Icon className="w-3 h-3 text-slate-500" />
          <span className="text-[10px] text-slate-500 uppercase tracking-wide">
            {condition.type === "prompt" ? "AI" : "Metric"}
          </span>
        </div>
        <p className="text-[11px] text-slate-300 truncate mt-0.5">
          {formatConditionLabel(condition)}
        </p>
        {condition.type === "deterministic" && condition.currentValue !== undefined && (
          <div className="text-[9px] text-slate-500 mt-0.5">
            Current: {condition.currentValue.toLocaleString()}{" "}
            {condition.operator && condition.threshold !== undefined && (
              <span className={condition.isMet ? "text-emerald-400/70" : "text-red-400/70"}>
                ({getOperatorSymbol(condition.operator)} {condition.threshold.toLocaleString()})
              </span>
            )}
          </div>
        )}
        {condition.type === "prompt" && condition.aiAssessment && (
          <div className="text-[9px] text-slate-500 mt-0.5">
            Confidence: {(condition.aiAssessment.confidence * 100).toFixed(0)}%
          </div>
        )}
      </div>
      {onClick && (
        <ChevronRight className="w-4 h-4 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5" />
      )}
    </button>
  );
};

export const ThesisConditionList: React.FC<ThesisConditionListProps> = ({
  conditions,
  maxVisible = 5,
  onConditionClick,
  compact = false,
}) => {
  const metConditions = conditions.filter((c) => c.isMet);
  const unmetConditions = conditions.filter((c) => !c.isMet);

  // Show unmet conditions first, then met conditions
  const sortedConditions = [...unmetConditions, ...metConditions];
  const visibleConditions = sortedConditions.slice(0, maxVisible);
  const hiddenCount = sortedConditions.length - maxVisible;

  if (conditions.length === 0) {
    return (
      <div className="flex items-center justify-center py-4">
        <span className="text-[11px] text-slate-500">No conditions defined</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Summary bar */}
      <div className="flex items-center gap-2 mb-2 px-1">
        <div className="flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3 text-emerald-400" />
          <span className="text-[10px] text-emerald-400 font-medium">
            {metConditions.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <XCircle className="w-3 h-3 text-red-400" />
          <span className="text-[10px] text-red-400 font-medium">
            {unmetConditions.length}
          </span>
        </div>
        <span className="text-[10px] text-slate-500">
          of {conditions.length} total
        </span>
      </div>

      {/* Condition list */}
      <div className="space-y-0.5">
        {visibleConditions.map((condition) => (
          <ConditionItem
            key={condition.id}
            condition={condition}
            onClick={onConditionClick ? () => onConditionClick(condition.id) : undefined}
            compact={compact}
          />
        ))}
      </div>

      {/* Hidden count */}
      {hiddenCount > 0 && (
        <div className="mt-1 px-1">
          <span className="text-[10px] text-slate-500">
            +{hiddenCount} more condition{hiddenCount > 1 ? "s" : ""}
          </span>
        </div>
      )}
    </div>
  );
};

export default ThesisConditionList;
